/**
 * Camera — pure 2D view transform (no PixiJS dependency, so it is unit-testable
 * and shared by rendering, picking and the minimap).
 *
 * Model: the camera is centred on a world-space focus point at a given `zoom`.
 * A world point P maps to the screen as:
 *     screen = viewport/2 + (P - focus) * zoom
 *
 * Zoom is smoothed toward `targetZoom` each frame; when the user zooms with the
 * wheel we anchor the world point under the cursor so it stays put (classic RTS
 * "zoom to cursor").
 */
import { clamp } from '@settlers/shared';
import type { Vec2 } from '@settlers/shared';

export interface CameraBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_SMOOTHING = 0.18;

export class Camera {
  focusX = 0;
  focusY = 0;
  zoom = 1;
  private targetZoom = 1;
  viewportW = 1;
  viewportH = 1;

  private bounds: CameraBounds = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  private anchor: { screenX: number; screenY: number; worldX: number; worldY: number } | null = null;

  setViewport(w: number, h: number): void {
    this.viewportW = Math.max(1, w);
    this.viewportH = Math.max(1, h);
    this.clampFocus();
  }

  setBounds(bounds: CameraBounds): void {
    this.bounds = bounds;
    this.clampFocus();
  }

  centerOn(worldX: number, worldY: number): void {
    this.focusX = worldX;
    this.focusY = worldY;
    this.clampFocus();
  }

  /** Pan by a screen-space delta (e.g. drag). */
  panByScreen(dxScreen: number, dyScreen: number): void {
    this.focusX -= dxScreen / this.zoom;
    this.focusY -= dyScreen / this.zoom;
    this.clampFocus();
  }

  /** Request a zoom step centred on a screen point (wheel). */
  zoomAt(screenX: number, screenY: number, factor: number): void {
    this.targetZoom = clamp(this.targetZoom * factor, MIN_ZOOM, MAX_ZOOM);
    const world = this.screenToWorld(screenX, screenY);
    this.anchor = { screenX, screenY, worldX: world.x, worldY: world.y };
  }

  setZoom(z: number): void {
    this.targetZoom = clamp(z, MIN_ZOOM, MAX_ZOOM);
  }

  /** Advance smoothing. `dtMs` keeps easing framerate-independent. */
  update(dtMs: number): void {
    const t = 1 - Math.pow(1 - ZOOM_SMOOTHING, dtMs / 16.6667);
    this.zoom += (this.targetZoom - this.zoom) * t;
    if (Math.abs(this.targetZoom - this.zoom) < 0.0005) this.zoom = this.targetZoom;

    if (this.anchor) {
      // Keep the anchored world point under the same screen pixel as we zoom.
      this.focusX = this.anchor.worldX - (this.anchor.screenX - this.viewportW / 2) / this.zoom;
      this.focusY = this.anchor.worldY - (this.anchor.screenY - this.viewportH / 2) / this.zoom;
      this.clampFocus();
      if (this.zoom === this.targetZoom) this.anchor = null;
    }
  }

  worldToScreen(worldX: number, worldY: number): Vec2 {
    return {
      x: this.viewportW / 2 + (worldX - this.focusX) * this.zoom,
      y: this.viewportH / 2 + (worldY - this.focusY) * this.zoom,
    };
  }

  screenToWorld(screenX: number, screenY: number): Vec2 {
    return {
      x: this.focusX + (screenX - this.viewportW / 2) / this.zoom,
      y: this.focusY + (screenY - this.viewportH / 2) / this.zoom,
    };
  }

  /** Visible world-space rectangle (for culling). */
  visibleWorldRect(): { x: number; y: number; w: number; h: number } {
    const halfW = this.viewportW / 2 / this.zoom;
    const halfH = this.viewportH / 2 / this.zoom;
    return {
      x: this.focusX - halfW,
      y: this.focusY - halfH,
      w: halfW * 2,
      h: halfH * 2,
    };
  }

  private clampFocus(): void {
    this.focusX = clamp(this.focusX, this.bounds.minX, this.bounds.maxX);
    this.focusY = clamp(this.focusY, this.bounds.minY, this.bounds.maxY);
  }
}
